import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles.css";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../firebaseConfig";
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
  sendPasswordResetEmail,
} from "firebase/auth";

function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // extras
  const [emailFromLink, setEmailFromLink] = useState("");
  const [codeValid, setCodeValid] = useState(false);

  const passwordRequirements = [
    { regex: /.{8,}/, message: "Must be at least 8 characters long." },
    { regex: /[A-Z]/, message: "Must have one uppercase letter." },
    { regex: /[a-z]/, message: "Must have one lowercase letter." },
    { regex: /\d/, message: "Must have one number." },
    { regex: /[@$!%*?&]/, message: "Must have one special character." },
  ];

  // Validate oobCode on mount; get email from Firebase
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    const oobCode = params.get("oobCode");

    setError("");
    setSuccess("");
    setCodeValid(false);

    if (mode !== "resetPassword") {
      setError("This link is not a password-reset link.");
      return;
    }
    if (!oobCode) {
      setError("Missing reset code. Please open the link from your email.");
      return;
    }

    (async () => {
      try {
        // verify code; returns the email this reset applies to
        const email = await verifyPasswordResetCode(auth, oobCode);
        setEmailFromLink(email);
        setCodeValid(true);
      } catch (err) {
        const msg =
          err?.code === "auth/expired-action-code"
            ? "This reset link has expired."
            : err?.code === "auth/invalid-action-code"
            ? "This reset link is invalid or already used."
            : (err?.message || "Unable to verify reset link.")
                .replace(/Firebase:\s*/i, "");
        setError(msg);
      }
    })();
  }, [location.search]);

  useEffect(() => {
    if (!password && !confirmPassword) {
      setPasswordMatchError("");
      return;
    }
    setPasswordMatchError(password === confirmPassword ? "" : "Passwords do not match.");
  }, [password, confirmPassword]);

  function handlePasswordChange(e) {
    const next = e.target.value;
    setPassword(next);
    const unmet = passwordRequirements
      .filter((r) => !r.regex.test(next))
      .map((r) => r.message);
    setPasswordErrors(unmet);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!codeValid) return;
    if (passwordErrors.length > 0) return;
    if (password !== confirmPassword) {
      setPasswordMatchError("Passwords do not match.");
      return;
    }

    const params = new URLSearchParams(location.search);
    const oobCode = params.get("oobCode");
    if (!oobCode) {
      setError("Missing reset code. Please use the link from your email.");
      return;
    }

    try {
      setLoading(true);
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess("Password has been reset. You can now log in.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const msg = (err?.message || "Failed to reset password.").replace(/Firebase:\s*/i, "");
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError("");
    setSuccess("");
    try {
      if (!emailFromLink) {
        setError("Cannot resend. Email unknown—request a new reset from the login page.");
        return;
      }
      await sendPasswordResetEmail(auth, emailFromLink);
      setSuccess(`A fresh reset link was sent to ${emailFromLink}.`);
    } catch (err) {
      const msg = (err?.message || "Failed to send reset email.").replace(/Firebase:\s*/i, "");
      setError(msg);
    }
  }

  return (
    <div className="container-fluid d-flex align-items-center justify-content-center">
      <div className="row w-100">
        <div
          className="col-md-6 d-none d-md-block p-5 position-relative text-white"
          style={{ backgroundColor: "#66d1be" }}
        />
        <div className="col-md-6 bg-light p-5">
          <h2 className="text-center mb-4">
            EcomElevate <br /> <small className="lead">Reset Your Password</small>
          </h2>

          <div className="mb-3 small">
            {emailFromLink && (
              <div className="text-muted">
                Resetting password for: <strong>{emailFromLink}</strong>
              </div>
            )}
            {!codeValid && (
              <div className="mt-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleResend}>
                  Resend reset email
                </button>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <fieldset disabled={!codeValid}>
              <div className="form-group">
                <label
                  htmlFor="password"
                  style={{
                    color: passwordErrors.length === 0 && password ? "green" : "black",
                  }}
                >
                  New Password
                </label>
                <input
                  type="password"
                  className="form-control custom-input"
                  id="password"
                  placeholder="Password"
                  autoComplete="off"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                />
                {passwordErrors.map((err, i) => (
                  <small key={i} className="text-danger d-block">
                    {err}
                  </small>
                ))}
              </div>

              <div className="form-group">
                <label
                  htmlFor="confirmPassword"
                  style={{
                    color: passwordMatchError
                      ? "red"
                      : confirmPassword && !passwordMatchError
                      ? "green"
                      : "black",
                  }}
                >
                  Confirm Password
                </label>
                <input
                  type="password"
                  className="form-control custom-input"
                  id="confirmPassword"
                  placeholder="Password"
                  autoComplete="off"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                {passwordMatchError && (
                  <small className="text-danger">{passwordMatchError}</small>
                )}
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-primary rounded-pill w-100"
                  disabled={
                    loading ||
                    !codeValid ||
                    passwordErrors.length > 0 ||
                    password !== confirmPassword
                  }
                >
                  {loading ? (
                    <>
                      <div className="spinner-grow" role="status" aria-hidden="true" />
                      <span className="sr-only">Loading...</span>
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              </div>
            </fieldset>

            {error && <div className="text-center text-danger mt-3">{error}</div>}
            {success && <div className="text-center text-success mt-3">{success}</div>}

            <div className="text-center mt-4 d-md-none">
              <p>
                Remembered your password?{" "}
                <span
                  className="text-uppercase fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/login")}
                >
                LOGIN
                </span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
